import { useTranslation } from 'react-i18next';
import { Building2, Check, Search } from 'lucide-react';
import { DebouncedInput } from 'components/blocks/debounced-input/debounced-input';
import { useState, useMemo } from 'react';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { OrganizationData } from 'features/organization/services/organization-service';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
    CommandSeparator,
} from 'components/ui/command';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';

interface IamTableOrganizationFilterProps {
    selectedIds: string[];
    onSelectionChange: (value: string[]) => void;
    title: string;
    className?: string;
}

export const IamTableOrganizationFilter: React.FC<IamTableOrganizationFilterProps> = ({
    selectedIds,
    onSelectionChange,
    title,
    className,
}) => {
    const { t } = useTranslation();
    const [searchOrganization, setSearchOrganization] = useState('');
    const queryParams = {
        page: 0,
        pageSize: 10,
        name: searchOrganization,
    };

    // Fetch organizations based on query parameters
    const { data } = useOrganizations(queryParams);

    const organizations: OrganizationData[] = useMemo(() => {
        if (!data?.data) return [];
        return data.data;
    }, [data?.data]);

    return (
        <Popover>
            <PopoverTrigger className={`rounded-3xl border border-primary text-sm ${className ?? 'w-[180px]'}`} asChild>
                <Button variant='outline' size="sm" className="flex items-center gap-2 h-10 border border-dashed rounded-3xl">
                    <div className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4" />
                        <span className={`text-xs font-medium`}>{title}</span>
                    </div>
                </Button>
            </PopoverTrigger>
            <PopoverContent>
                <Command>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="sticky flex items-start border-b"
                    >
                        <Search className="mr-2 ml-2 h-4 w-4 shrink-0 opacity-50 mt-4" />
                        <DebouncedInput
                            placeholder={t("SEARCH_BY_ORGANIZATION")}
                            onDebouncedChange={setSearchOrganization}
                            initialValue={searchOrganization}
                            className="w-full rounded-none border-0 !ring-0 !ring-offset-0"
                            isShowInputLength={false}
                        />
                    </div>

                    <CommandList>
                        <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
                        <CommandGroup>
                            {organizations.map((option) => {
                                const isSelected = selectedIds?.includes(option.itemId);
                                return (
                                    <CommandItem
                                        key={option.itemId}
                                        onSelect={() => {
                                            if (isSelected) {
                                                onSelectionChange(selectedIds?.filter((id) => id !== option.itemId));
                                            } else {
                                                onSelectionChange([...selectedIds, option.itemId]);
                                            }
                                        }}
                                    >
                                        <div
                                            className={cn(
                                                'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                                isSelected ? 'bg-primary text-white' : 'opacity-50 [&_svg]:invisible'
                                            )}
                                        >
                                            <Check />
                                        </div>
                                        <span>{t(option.name)}</span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                        {selectedIds.length > 0 && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        onSelect={() => onSelectionChange([])}
                                        className="justify-center text-center"
                                    >
                                        {t('CLEAR_FILTERS')}
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
};